import Button from "../../components/base/Button";
import { useState } from "react";
import {
  useSEO,
  generateProductSchema,
  generateWebPageSchema,
} from "../../utils/seo";

export default function MovePipe() {
  const [isCalendlyModalOpen, setIsCalendlyModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const openCalendlyModal = () => setIsCalendlyModalOpen(true);
  const closeCalendlyModal = () => setIsCalendlyModalOpen(false);
  const openContactModal = () => setIsContactModalOpen(true);
  const closeContactModal = () => setIsContactModalOpen(false);

  useSEO({
    title: "MovePipe - Automação de fluxos de trabalho no-code | Moveideias",
    description:
      "Ferramenta de automação de fluxos de trabalho que conecta sistemas e padroniza processos internos. Tecnologia no-code para processos logísticos adjacentes.",
    keywords:
      "MovePipe, automação, workflow, no-code, processos, logística reversa, auditoria de frete, BPM",
    canonical: "/movepipe",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateProductSchema({
          name: "MovePipe",
          description:
            "Ferramenta de automação de fluxos de trabalho que conecta diferentes sistemas e padroniza processos internos da empresa.",
          url: "/movepipe",
        }),
        generateWebPageSchema({
          name: "MovePipe - Automação de Fluxos de Trabalho",
          description:
            "Automação inteligente de processos com tecnologia no-code.",
          url: "/movepipe",
        }),
      ],
    },
  });

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(rgba(90, 106, 176, 0.6), rgba(90, 106, 176, 0.6)), url('/images/products/movepipe/hero-background.png')",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center w-full">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            MovePipe: automação inteligente de fluxos de trabalho
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 leading-relaxed">
            Conecte sistemas, padronize processos e elimine tarefas manuais com
            automação de ponta a ponta.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openCalendlyModal}
            >
              Solicitar demonstração
            </Button>
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openContactModal}
            >
              Fale conosco
            </Button>
          </div>
        </div>
      </section>

      {/* Principais Benefícios */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Transforme processos manuais em fluxos digitais
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              O MovePipe utiliza tecnologia no-code para criar fluxos
              automatizados que eliminam planilhas, padronizam operações e
              empoderam gestores.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/eficiencia-operacional-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-speed-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Eficiência operacional
                </h3>
                <p className="text-gray-600">
                  Redução de tempo, esforço e retrabalho através da automação de
                  tarefas manuais e eliminação de processos redundantes.
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/agilidade-na-adaptacao-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-rocket-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Agilidade na adaptação
                </h3>
                <p className="text-gray-600">
                  Criação rápida de fluxos com autonomia para o time de negócio,
                  sem depender de longos ciclos de desenvolvimento.
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/rastreabilidade-completa-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-history-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Rastreabilidade completa
                </h3>
                <p className="text-gray-600">
                  Histórico completo de todas as etapas do processo com
                  visibilidade total de status e ações realizadas.
                </p>
              </div>
            </div>

            {/* Card 4 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/conformidade-e-governanca-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Conformidade e governança
                </h3>
                <p className="text-gray-600">
                  Segurança, controle de acesso e auditabilidade com processos
                  estruturados e rastreáveis.
                </p>
              </div>
            </div>

            {/* Card 5 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/dados-e-indicadores-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-bar-chart-box-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Dados e indicadores
                </h3>
                <p className="text-gray-600">
                  Informações organizadas para análise e melhoria contínua com
                  dashboards e relatórios operacionais.
                </p>
              </div>
            </div>

            {/* Card 6 */}
            <div className="bg-[#e7eaf6] p-8 rounded-xl text-center relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-10 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/escalabilidade-e-integracao-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-expand-diagonal-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-3">
                  Escalabilidade e integração
                </h3>
                <p className="text-gray-600">
                  Adapta-se a diferentes áreas, processos e tamanhos de
                  operação, unificando sistemas em fluxos organizados.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problemas que Resolve */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(231, 234, 246, 0.95), rgba(231, 234, 246, 0.95)), url('/images/products/movepipe/problemas-resolve.png')",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Problemas críticos que o MovePipe resolve
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Transforme processos manuais e descentralizados em fluxos digitais
              padronizados e automatizados.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Processos manuais
                  </h3>
                  <p className="text-gray-600">
                    Controles em planilhas, e-mails desorganizados e fluxos
                    manuais que geram lentidão e erros.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Digitalização completa
                  </h3>
                  <p className="text-gray-600">
                    Fluxos digitais automatizados com validações, prazos e
                    alertas automáticos.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Lentidão e falta de fluidez
                  </h3>
                  <p className="text-gray-600">
                    Processos travados, sem prazos definidos e sem alertas para
                    acompanhamento.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Fluxo ágil e controlado
                  </h3>
                  <p className="text-gray-600">
                    Definição de prazos, alertas e validações automáticas que
                    garantem fluidez.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Falta de padronização
                  </h3>
                  <p className="text-gray-600">
                    Cada pessoa executa de um jeito, sem regras claras ou
                    obrigatórias.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Execução estruturada
                  </h3>
                  <p className="text-gray-600">
                    Execução estruturada com regras claras e obrigatórias para
                    todos.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Falta de rastreabilidade
                  </h3>
                  <p className="text-gray-600">
                    Impossível saber quem fez o quê, quando e por que cada ação
                    foi tomada.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Visibilidade completa
                  </h3>
                  <p className="text-gray-600">
                    Visibilidade completa, status e histórico detalhado de todas
                    as ações.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Comunicação desorganizada
                  </h3>
                  <p className="text-gray-600">
                    Comunicação entre áreas dispersa em e-mails, mensagens e
                    conversas informais.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Comunicação fluida
                  </h3>
                  <p className="text-gray-600">
                    Comunicação fluida e registrada dentro do próprio fluxo de
                    trabalho.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Dependência da TI
                  </h3>
                  <p className="text-gray-600">
                    Qualquer ajuste ou mudança depende de longos ciclos de
                    desenvolvimento.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Autonomia para o negócio
                  </h3>
                  <p className="text-gray-600">
                    Plataforma no/low-code com autonomia para o time de negócio
                    criar e ajustar.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-close-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-red-600 mb-2">
                    PROBLEMA: Riscos de segurança
                  </h3>
                  <p className="text-gray-600">
                    Processos sem controle de acesso adequado e não conformidade
                    com governança.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-green-500">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className="ri-check-line text-xl text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-600 mb-2">
                    SOLUÇÃO: Governança e segurança
                  </h3>
                  <p className="text-gray-600">
                    Controle de acesso, histórico e estrutura alinhada à
                    governança e auditoria.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-lg text-gray-700 max-w-4xl mx-auto">
              O MovePipe elimina entraves operacionais, tornando os processos
              ágeis, padronizados e controlados. Transforma a operação em uma
              estrutura digital e orientada por dados, dando aos gestores
              autonomia para adaptar, escalar e melhorar com eficiência e
              segurança.
            </p>
          </div>
        </div>
      </section>

      {/* Impactos nas Áreas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#221a32] mb-6">
              Impactos transformadores em toda a empresa
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              O MovePipe gera mudanças estruturais que beneficiam todas as áreas
              da organização.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Impacto 1 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/eficiencia-op-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-settings-3-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Eficiência operacional
                </h3>
                <p className="text-gray-600">
                  Redução de tarefas manuais, retrabalho e tempo de ciclo
                  através da automação inteligente.
                </p>
              </div>
            </div>

            {/* Impacto 2 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/governanca-e-conformidade-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-shield-check-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Governança e conformidade
                </h3>
                <p className="text-gray-600">
                  Processos estruturados, rastreáveis e auditáveis com controle
                  total de acesso.
                </p>
              </div>
            </div>

            {/* Impacto 3 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/cultura-de-dados-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-bar-chart-box-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Cultura de dados
                </h3>
                <p className="text-gray-600">
                  Indicadores de performance e dados para análise e tomada de
                  decisão estratégica.
                </p>
              </div>
            </div>

            {/* Impacto 4 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/agilidade-organizacional-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-rocket-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Agilidade organizacional
                </h3>
                <p className="text-gray-600">
                  Fluxos adaptáveis criados sem depender da TI, com autonomia
                  para o negócio.
                </p>
              </div>
            </div>

            {/* Impacto 5 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/colaboracao-entre-areas-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-team-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Colaboração entre áreas
                </h3>
                <p className="text-gray-600">
                  Fluxos integrados e visibilidade compartilhada entre
                  diferentes departamentos.
                </p>
              </div>
            </div>

            {/* Impacto 6 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/seguranca-e-rastreabilidade-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-history-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Segurança e rastreabilidade
                </h3>
                <p className="text-gray-600">
                  Histórico completo de ações, decisões e responsabilidades em
                  cada etapa.
                </p>
              </div>
            </div>

            {/* Impacto 7 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/escalabilidade-e-sustentacao-bg.png')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-expand-diagonal-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Escalabilidade e sustentação
                </h3>
                <p className="text-gray-600">
                  Plataforma flexível que cresce com o negócio, adaptando-se às
                  novas demandas.
                </p>
              </div>
            </div>

            {/* Impacto 8 */}
            <div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-5 bg-cover bg-center"
                style={{
                  backgroundImage:
                    "url('/images/products/movepipe/gestao-do-conhecimento-bg.jpg')",
                }}
              />
              <div className="relative z-10">
                <div className="w-16 h-16 bg-[#5a6ab0] rounded-full flex items-center justify-center mb-6">
                  <i className="ri-book-open-line text-2xl text-white" />
                </div>
                <h3 className="text-xl font-bold text-[#221a32] mb-4">
                  Gestão do conhecimento
                </h3>
                <p className="text-gray-600">
                  Preservação do conhecimento tácito, disseminação estruturada e
                  onboarding facilitado.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Processos Logísticos Adjacentes */}
      <section
        className="py-20 bg-cover bg-center relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(90, 106, 176, 0.9), rgba(90, 106, 176, 0.9)), url('/images/backgrounds/logistics-processes.jpg')",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Processos logísticos que o MovePipe otimiza
            </h2>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Automatize processos adjacentes à logística com fluxos
              estruturados e rastreáveis.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-arrow-go-back-line text-2xl text-[#5a6ab0]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">
                Logística reversa
              </h3>
              <p className="text-white/80 text-sm">
                Fluxo estruturado com aprovações, controle e rastreabilidade
                completa.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-refund-line text-2xl text-[#5a6ab0]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">
                Gestão de devoluções
              </h3>
              <p className="text-white/80 text-sm">
                Tratamento automatizado de devoluções com análise de causa raiz.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-file-search-line text-2xl text-[#5a6ab0]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">
                Auditoria de frete
              </h3>
              <p className="text-white/80 text-sm">
                Fluxo de auditoria com validações automáticas e análise de
                inconsistências.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-exchange-funds-line text-2xl text-[#5a6ab0]" />
              </div>
              <h3 className="text-lg font-bold text-white mb-3">
                Reembolsos e estornos
              </h3>
              <p className="text-white/80 text-sm">
                Processo estruturado para solicitação e aprovação de reembolsos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section
        className="py-16 bg-cover bg-center relative"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 26, 50, 0.9), rgba(34, 26, 50, 0.9)), url('/images/products/movepipe/cta-final.jpeg')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Transforme seus processos com o MovePipe
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
            Automatize, padronize e digitalize fluxos logísticos com uma solução
            no-code poderosa e intuitiva.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openCalendlyModal}
            >
              Solicitar demonstração
            </Button>
            <Button
              size="lg"
              className="bg-[#f28c1a] text-white hover:bg-[#cc7013] transition-colors duration-200 whitespace-nowrap cursor-pointer"
              onClick={openContactModal}
            >
              Fale conosco
            </Button>
          </div>
        </div>
      </section>

      {/* Modal Calendly */}
      {isCalendlyModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeCalendlyModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Agendar demonstração
              </h3>
              <button
                onClick={closeCalendlyModal}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://calendly.com/move-comercial/60min"
                className="w-full h-full"
                title="Agendar demonstração"
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal Fale Conosco */}
      {isContactModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={closeContactModal}
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-[#221a32]">
                Fale conosco
              </h3>
              <button
                onClick={closeContactModal}
                className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200 cursor-pointer"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>
            <div className="h-[600px]">
              <iframe
                src="https://portal.moveideias.com/workflow/public/form/9ec92cd7-ac11-4a04-8a8a-6d71c473e303"
                className="w-full h-full"
                title="Formulário de contato"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
